/**
 * Signed-URL photo upload transport — the single "PUT a blob to a GCS v4
 * signed URL" implementation shared by every OfficeHub field PWA.
 *
 * This is the header-correct uploader (audit finding A3): the v4 signed URL is
 * minted with an `x-goog-content-length-range` extension header baked into the
 * signature, so the client MUST send the identical header on the PUT or GCS
 * rejects the request. Both the desktop CRA client and the offline-first Sales
 * PWA route their signed-URL PUTs through this module, so the header (and the
 * timeout / retry policy) can never drift between the two apps again.
 *
 * Framework-free by design: no Firebase, no React. Callers get the signed URL
 * from their own app-bound callable and hand the raw blob to these primitives.
 */

// This transport is used for field capture over flaky connectivity. The
// signed-URL PUT goes straight to GCS via raw XHR (no SDK-level timeout, unlike
// the httpsCallable calls around it), so without an explicit timeout a stalled
// upload can hang indefinitely with no way for the caller to fail/retry it.
export const SURVEY_UPLOAD_TIMEOUT_MS = 60_000;

// Must match the `x-goog-content-length-range` extension header the backend
// signs into the URL (functions/src/survey/services/photoUploadService.ts,
// `MAX_UPLOAD_BYTES = 10 * 1024 * 1024`). V4 signed URLs that include
// extensionHeaders at signing time require the client to send the identical
// header on the actual request, or GCS may reject the signature / silently skip
// enforcing the constraint. Expressed as `0,<bytes>` from the same 10 MiB math.
export const SURVEY_CONTENT_LENGTH_RANGE_HEADER = '0,10485760';

export interface GetUploadUrlResult {
  uploadUrl: string;
  storageRef: string;
  photoId: string;
}

export interface UploadProgress {
  current: number;
  total: number;
}

export interface UploadBlobOptions {
  onProgress?: (progress: UploadProgress) => void;
  timeoutMs?: number;
  /**
   * The `x-goog-content-length-range` header value. Defaults to
   * {@link SURVEY_CONTENT_LENGTH_RANGE_HEADER}; override only if the backend's
   * signed range ever diverges (it should not — keep them in lockstep).
   */
  contentLengthRange?: string;
}

/** An upload error carrying the HTTP status so retry logic can classify it. */
export type UploadError = Error & { status?: number };

/**
 * PUT a blob to a GCS v4 signed URL with a timeout and optional progress
 * reporting. Used for both the fire-and-forget intake flow and the
 * progress-tracked section-photo flow. Uses XHR (not fetch) because only XHR
 * exposes native upload-progress events.
 */
export function uploadBlobToSignedUrl(
  uploadUrl: string,
  blob: Blob,
  contentType: string,
  options: UploadBlobOptions = {},
): Promise<void> {
  const timeoutMs = options.timeoutMs ?? SURVEY_UPLOAD_TIMEOUT_MS;
  const contentLengthRange = options.contentLengthRange ?? SURVEY_CONTENT_LENGTH_RANGE_HEADER;

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.setRequestHeader('x-goog-content-length-range', contentLengthRange);
    xhr.timeout = timeoutMs;

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        options.onProgress?.({ current: event.loaded, total: event.total });
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        // Attach the status so the retry wrapper can distinguish a permanent
        // 4xx (bad signature/permission — don't retry) from a transient 5xx.
        const err = new Error(
          `Photo upload failed: ${xhr.status} ${xhr.statusText}`,
        ) as UploadError;
        err.status = xhr.status;
        reject(err);
      }
    };
    xhr.onerror = () => reject(new Error('Photo upload failed: network error'));
    xhr.ontimeout = () => reject(new Error(`Photo upload timed out after ${timeoutMs}ms`));
    xhr.send(blob);
  });
}

/** Five tries, waiting 2s, 4s, 8s, 16s between them (about 30 s). The signed URL lives ~15 min. */
export const UPLOAD_RETRY_ATTEMPTS = 5;
export const UPLOAD_RETRY_BASE_DELAY_MS = 2000;

/** An early retry on regained signal still waits this long, so a radio that is flapping
 * online/offline cannot burn every attempt in a few seconds. */
export const UPLOAD_RETRY_MIN_WAIT_MS = 500;

/**
 * Wait `ms`; when `canSkip` is true and the phone regains signal, stop early (after at
 * least UPLOAD_RETRY_MIN_WAIT_MS) so the retry happens as soon as it can succeed.
 */
function waitForRetry(ms: number, canSkip: boolean): Promise<void> {
  return new Promise((resolve) => {
    const hasWindow = typeof window !== 'undefined';
    const startedAt = Date.now();
    const finish = () => {
      clearTimeout(timer);
      if (hasWindow) window.removeEventListener('online', onOnline);
      resolve();
    };
    const onOnline = () => {
      const left = UPLOAD_RETRY_MIN_WAIT_MS - (Date.now() - startedAt);
      if (left <= 0) finish();
      else setTimeout(finish, left);
    };
    const timer = setTimeout(finish, ms);
    if (hasWindow && canSkip) window.addEventListener('online', onOnline);
  });
}

/**
 * PUT a blob to the signed URL, retrying transient failures (network errors,
 * timeouts, 5xx) with exponential backoff -- mirrors the FSR photo-upload
 * pattern so a flaky-signal field capture doesn't drop photos on the first
 * hiccup (SALES-7: three tries over ~3 s gave up before weak signal came back).
 * A retry fires at once when the phone comes back online. A 4xx (bad signature /
 * permission, except 429) is permanent and fails immediately.
 */
export async function uploadBlobToSignedUrlWithRetry(
  uploadUrl: string,
  blob: Blob,
  contentType: string,
  options: UploadBlobOptions & { attempts?: number } = {},
): Promise<void> {
  const attempts = options.attempts ?? UPLOAD_RETRY_ATTEMPTS;
  let lastErr: unknown;
  let skippedWait = false; // signal regained shortens ONE wait per upload, not every wait
  for (let i = 0; i < attempts; i++) {
    try {
      await uploadBlobToSignedUrl(uploadUrl, blob, contentType, options);
      return;
    } catch (err) {
      lastErr = err;
      const status = (err as UploadError).status;
      // 4xx is permanent (except 429 rate-limit) -- don't waste retries.
      if (status && status >= 400 && status < 500 && status !== 429) throw err;
      if (i < attempts - 1) {
        const startedWaiting = Date.now();
        await waitForRetry(UPLOAD_RETRY_BASE_DELAY_MS * 2 ** i, !skippedWait);
        if (Date.now() - startedWaiting < UPLOAD_RETRY_BASE_DELAY_MS * 2 ** i) skippedWait = true;
      }
    }
  }
  throw lastErr;
}
